import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trophy, Medal, Award, ArrowLeft, Crown } from 'lucide-react';
import { getLeaderboard } from '../utils/api';

export default function Leaderboard({ user }) {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuestModal, setShowGuestModal] = useState(false);

  useEffect(() => {
    // Only show guest modal if user is not logged in AND guest_mode is true
    const isGuest = localStorage.getItem('guest_mode') === 'true' && !user;
    if (isGuest) {
      setShowGuestModal(true);
    }
    
    loadLeaderboard();
  }, [user]);

  const loadLeaderboard = async () => {
    try {
      const response = await getLeaderboard();
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: <Trophy className="w-6 h-6 sm:w-8 sm:h-8" />, color: 'text-yellow-500', bg: 'bg-yellow-50' };
    if (rank === 2) return { icon: <Medal className="w-6 h-6 sm:w-8 sm:h-8" />, color: 'text-gray-400', bg: 'bg-gray-50' };
    if (rank === 3) return { icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />, color: 'text-orange-600', bg: 'bg-orange-50' };
    return { icon: null, color: 'text-gray-600', bg: 'bg-white' };
  };

  const handleLoginClick = () => {
    navigate('/auth');
  };

  const handleContinueAsGuest = () => {
    setShowGuestModal(false);
  };

  return (
    <div 
      className="min-h-screen py-6 sm:py-12 px-4 sm:px-6 relative"
      data-testid="leaderboard-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-4 sm:mb-6 text-white hover:bg-white/20 min-h-[44px]"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>Back</span>
        </Button>

        <div className="text-center mb-8 sm:mb-12">
          <div className="flex justify-center mb-4 sm:mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
              alt="Mega Church Logo"
              className="w-48 h-48 sm:w-60 sm:h-60 object-contain"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4" data-testid="leaderboard-title">
            Leaderboard
          </h1>
          <p className="text-base sm:text-xl text-white/90">Top Players by Members</p>
        </div>

        {loading ? (
          <Card className="bg-white/95 backdrop-blur-lg p-8 sm:p-12 text-center rounded-3xl">
            <p className="text-lg sm:text-xl text-gray-600">Loading leaderboard...</p>
          </Card>
        ) : leaderboard.length === 0 ? (
          <Card className="bg-white/95 backdrop-blur-lg p-8 sm:p-12 text-center rounded-3xl">
            <p className="text-lg sm:text-xl text-gray-600">No players yet. Be the first!</p>
          </Card>
        ) : (
          <Card className="bg-white/95 backdrop-blur-lg rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] p-4 sm:p-6">
              <div className="grid grid-cols-12 gap-2 sm:gap-4 text-white font-bold text-xs sm:text-sm">
                <div className="col-span-2 text-center">Rank</div>
                <div className="col-span-6 sm:col-span-5">Player</div>
                <div className="col-span-4 sm:col-span-5 text-right">Members</div>
              </div>
            </div>

            {/* Leaderboard Items */}
            <div className="divide-y divide-gray-200">
              {leaderboard.map((player, index) => {
                const rank = index + 1;
                const isTopThree = rank <= 3;
                
                let rankBg = 'bg-gray-100';
                let rankColor = 'text-gray-600';
                if (rank === 1) { rankBg = 'bg-yellow-100'; rankColor = 'text-yellow-600'; }
                if (rank === 2) { rankBg = 'bg-gray-200'; rankColor = 'text-gray-600'; }
                if (rank === 3) { rankBg = 'bg-orange-100'; rankColor = 'text-orange-600'; }

                return (
                  <div
                    key={player.id || index}
                    data-testid={`player-${rank}`}
                    className={`grid grid-cols-12 gap-2 sm:gap-4 items-center p-3 sm:p-4 transition-colors ${
                      isTopThree ? 'bg-yellow-50/50 hover:bg-yellow-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Rank */}
                    <div className="col-span-2 flex justify-center">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 ${rankBg} ${rankColor} rounded-full flex items-center justify-center font-bold text-base sm:text-xl`}>
                        {rank === 1 && '🏆'}
                        {rank === 2 && '🥈'}
                        {rank === 3 && '🥉'}
                        {rank > 3 && rank}
                      </div>
                    </div>

                    {/* Player Info */}
                    <div className="col-span-6 sm:col-span-5 flex items-center gap-2 sm:gap-3 min-w-0">
                      {player.picture ? (
                        <img
                          src={player.picture}
                          alt={player.name}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white shadow object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#4169E1] to-[#9370DB] flex items-center justify-center text-white text-sm sm:text-base font-bold border-2 border-white shadow flex-shrink-0">
                          {player.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-extrabold text-gray-800 text-sm sm:text-base truncate" data-testid={`player-name-${rank}`}>
                          {player.name}
                        </h3>
                      </div>
                    </div>

                    {/* Members */}
                    <div className="col-span-4 sm:col-span-5 text-right">
                      <p className="text-xl sm:text-2xl font-bold text-black" data-testid={`player-members-${rank}`}>
                        {player.total_members}
                      </p>
                      <p className="text-xs text-gray-500 hidden sm:block">members</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Guest Modal */}
        <Dialog open={showGuestModal} onOpenChange={setShowGuestModal}>
          <DialogContent className="sm:max-w-md bg-white mx-4">
            <DialogHeader>
              <DialogTitle className="text-2xl sm:text-3xl text-[#4169E1]">Join the Leaderboard!</DialogTitle>
              <DialogDescription className="text-base sm:text-lg pt-4 text-gray-700">
                Log in to place on the leaderboard and keep your members.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-gray-800 mb-4 font-semibold text-sm sm:text-base">
                As a guest, you can play the game but won't appear on the leaderboard. Create an account to:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-gray-800 font-semibold text-sm sm:text-base">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Save your progress permanently</span>
                </li>
                <li className="flex items-center gap-2 text-gray-800 font-semibold text-sm sm:text-base">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Compete on the leaderboard</span>
                </li>
                <li className="flex items-center gap-2 text-gray-800 font-semibold text-sm sm:text-base">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Track your members across devices</span>
                </li>
              </ul>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={handleContinueAsGuest}
                className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 hover:bg-gray-50 min-h-[44px]"
              >
                <span>Continue as Guest</span>
              </Button>
              <Button
                onClick={handleLoginClick}
                className="w-full sm:w-auto bg-[#4169E1] hover:bg-[#3558c7] text-white font-bold min-h-[44px]"
              >
                <span>Log In</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
